export const BILLING_STATUS = {
  unpaid: "unpaid",
  partial: "partial",
  paid: "paid",
  cancelled: "cancelled",
} as const;

export type BillingStatus =
  (typeof BILLING_STATUS)[keyof typeof BILLING_STATUS];

/** Payment-derived status from totals — never includes `cancelled`. */
export type InvoicePaymentStatus = Exclude<
  BillingStatus,
  typeof BILLING_STATUS.cancelled
>;

export function deriveBillingStatus(
  total: number,
  totalPaid: number,
): InvoicePaymentStatus {
  const normalizedTotal = Number(total) || 0;
  const normalizedPaid = Number(totalPaid) || 0;

  if (normalizedTotal <= 0.005) {
    return BILLING_STATUS.unpaid;
  }
  if (normalizedPaid >= normalizedTotal - 0.005) {
    return BILLING_STATUS.paid;
  }
  if (normalizedPaid > 0.005) {
    return BILLING_STATUS.partial;
  }
  return BILLING_STATUS.unpaid;
}

export function isCancelledBillingDoc(doc: {
  status?: unknown;
}): boolean {
  return (
    String(doc.status ?? "").trim().toLowerCase() === BILLING_STATUS.cancelled
  );
}

export function normalizeBillingStatus(value: unknown): BillingStatus | "" {
  const raw = String(value ?? "").trim().toLowerCase();
  if (raw === BILLING_STATUS.paid) {
    return BILLING_STATUS.paid;
  }
  if (raw === BILLING_STATUS.partial) {
    return BILLING_STATUS.partial;
  }
  if (raw === BILLING_STATUS.unpaid) {
    return BILLING_STATUS.unpaid;
  }
  if (raw === BILLING_STATUS.cancelled) {
    return BILLING_STATUS.cancelled;
  }

  if (
    raw === "open" ||
    raw === "draft" ||
    raw === "overdue" ||
    raw === "current" ||
    raw === "void"
  ) {
    return BILLING_STATUS.unpaid;
  }
  if (raw === "partiallypaid") {
    return BILLING_STATUS.partial;
  }

  return "";
}

export function formatBillingStatusLabel(status: unknown): string {
  const normalized = normalizeBillingStatus(status);
  if (!normalized) {
    return String(status ?? "").trim();
  }
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
}
